import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { useFetchQuote } from '@features/invoices/hooks/useFetchQuote'
import { useUpdateApprovedQuoteMutation } from '@features/invoices/hooks/useUpdateApprovedQuoteMutation'
import { Quote } from '@features/invoices/types/quotes/quote'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { InvoiceEditorUrlParams } from '../../../types/invoiceEditorUrlParams'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { InvoiceEditorVariant } from '../enums/invoiceEditorVariant'
import { getApprovedQuoteOverrides } from '../utils/getApprovedQuoteOverrides'
import { useInvoiceEditorVariant } from './useInvoiceEditorVariant'

export const useUpdateApprovedQuote = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setDirty } = useDirtyRoute()
  const { organization } = useUserOrganization()
  const { documentId } = useParams<InvoiceEditorUrlParams>()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { invoiceEditorForm } = useInvoiceEditorForm()

  const isQuoteEdit = invoiceEditorVariant === InvoiceEditorVariant.QuoteEdit
  const { quote: currentQuote, isLoading: isLoadingQuote } = useFetchQuote(isQuoteEdit ? documentId : undefined)
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings({
    enabled: isQuoteEdit,
  })

  const { isProcessing, updateApprovedQuote: updateApprovedQuoteMutation } = useUpdateApprovedQuoteMutation({
    onSuccess: () => {
      setDirty(false)

      queryClient.invalidateQueries([QueryKeys.Quote, documentId])
      queryClient.invalidateQueries([QueryKeys.Quotes])
      queryClient.invalidateQueries([QueryKeys.QuotesSummary])

      notify({
        id: NotificationKeys.QuoteUpdated,
        message: t('quotes.editor.update.success'),
        variant: 'success',
      })

      if (documentId) {
        navigate(
          getOrganizationViewUrl(organization?.url, reactRoute.organization.quotes.getViewQuoteRoute(documentId)),
        )
      }
    },
    onError: () => {
      notify({
        id: NotificationKeys.QuoteUpdated,
        message: t('quotes.editor.update.error'),
        variant: 'error',
      })
    },
  })

  const updateApprovedQuote = useCallback(() => {
    if (!documentId || !currentQuote) {
      return
    }

    const formData = invoiceEditorForm.getValues()
    const configuredPaymentMethods = zervantSettings?.values?.paymentMethods ?? currentQuote.paymentMeans ?? []
    const overrides = getApprovedQuoteOverrides({
      configuredPaymentMethods,
      current: currentQuote,
      formData,
      units: zervantSettings?.managedFields?.UNIT_OPTIONS,
    })
    const payload: Quote = { ...currentQuote, ...overrides }

    updateApprovedQuoteMutation({ payload, quoteId: documentId })
  }, [
    currentQuote,
    documentId,
    invoiceEditorForm,
    updateApprovedQuoteMutation,
    zervantSettings?.managedFields?.UNIT_OPTIONS,
    zervantSettings?.values?.paymentMethods,
  ])

  // react-query v3 reports isLoading: true for disabled queries; only surface
  // the loading state when this hook is actually fetching a quote.
  return {
    isLoading: isQuoteEdit && (isLoadingQuote || isLoadingZervantSettings),
    isProcessing,
    updateApprovedQuote,
  }
}
